import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Upload, Loader2, ScanLine, ArrowRight, FileSpreadsheet, FileText, FileDown, Lock, Sparkles } from 'lucide-react';
import { toolsAPI } from '../utils/api';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const STRINGS = {
  en: {
    tryFree: 'Try it free — no sign-up',
    vpPre: 'Just snap or scan — our AI extracts your data into ', vpHi: 'Excel, PDF or CSV', vpPost: '.',
    errImg: 'Please upload a receipt image (JPG, PNG) or PDF.',
    errRead: "Couldn't read that image — try a clearer photo of a receipt.",
    errGeneric: 'Something went wrong. Please try again.',
    reading: 'Reading your receipt…',
    dropTitle: 'Drop a receipt or invoice here',
    dropSub: 'or click to upload — JPG, PNG or PDF',
    cappedTitle: "You've used your free scans for today",
    cappedSub: 'Sign up free to scan and export receipts (3 to try) to Excel, CSV & PDF.',
    signupFree: 'Sign up free',
    fMerchant: 'Merchant', fAmount: 'Amount', fDate: 'Date', fCategory: 'Category', fTax: 'Tax / VAT', fCurrency: 'Currency',
    downloadAs: 'Download this receipt as', csvFree: 'CSV — free',
    exportsFree: 'Excel & PDF exports are free — just sign up.',
    signupExport: 'Sign up free to export Excel & PDF',
    scanAnother: 'Scan another',
  },
  es: {
    tryFree: 'Pruébalo gratis — sin registro',
    vpPre: 'Solo toma una foto o escanea — nuestra IA extrae tus datos a ', vpHi: 'Excel, PDF o CSV', vpPost: '.',
    errImg: 'Sube una imagen de recibo (JPG, PNG) o un PDF.',
    errRead: 'No pudimos leer esa imagen — intenta con una foto más clara del recibo.',
    errGeneric: 'Algo salió mal. Inténtalo de nuevo.',
    reading: 'Leyendo tu recibo…',
    dropTitle: 'Arrastra un recibo o factura aquí',
    dropSub: 'o haz clic para subir — JPG, PNG o PDF',
    cappedTitle: 'Ya usaste tus escaneos gratis de hoy',
    cappedSub: 'Regístrate gratis para escanear y exportar recibos (3 de prueba) a Excel, CSV y PDF.',
    signupFree: 'Regístrate gratis',
    fMerchant: 'Comercio', fAmount: 'Monto', fDate: 'Fecha', fCategory: 'Categoría', fTax: 'Impuesto', fCurrency: 'Moneda',
    downloadAs: 'Descarga este recibo como', csvFree: 'CSV — gratis',
    exportsFree: 'Exportar a Excel y PDF es gratis — solo regístrate.',
    signupExport: 'Regístrate gratis para exportar a Excel y PDF',
    scanAnother: 'Escanear otro',
  },
};

// Free, no-login single-receipt scanner demo used on the public SEO pages.
export const TryScanner = ({ format = 'CSV', lang = 'en' }) => {
  const t = STRINGS[lang] || STRINGS.en;
  const inputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [capped, setCapped] = useState(false);

  const handleFile = async (file) => {
    if (!file || !(file.type.startsWith('image/') || file.type === 'application/pdf')) {
      setError(t.errImg);
      return;
    }
    setError(''); setResult(null); setScanning(true);
    try {
      const b64 = await fileToBase64(file);
      if (file.type.startsWith('image/')) setPreview(b64);
      const { data } = await toolsAPI.scanDemo(b64, file.type);
      setResult(data);
      if (data.remaining === 0) setCapped(true);
      if (!data.success) setError(data.message || t.errRead);
    } catch (e) {
      if (e.response?.status === 429) { setError(e.response.data.detail); setCapped(true); }
      else setError(t.errGeneric);
    } finally {
      setScanning(false);
    }
  };

  const downloadCsv = () => {
    if (!result) return;
    const rows = [
      ['Date', 'Merchant', 'Category', 'Amount', 'Currency', 'Tax'],
      [result.date || '', result.merchant || '', result.category || '', result.amount ?? '', result.currency || '', result.tax ?? ''],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'moneyfeed-sample-receipt.csv';
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); };
  const sym = { USD: '$', SAR: 'SAR ', AED: 'AED ', GBP: '£', CAD: 'C$', INR: '₹', PKR: 'Rs ' }[result?.currency] || '';

  return (
    <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-[#064E3B]/10" data-testid="try-scanner">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#84CC16]/15 text-[#3f6212] text-xs font-bold uppercase tracking-wider">
          <Sparkles className="h-3.5 w-3.5" /> {t.tryFree}
        </span>
      </div>

      <p className="text-[#064E3B] font-semibold mb-4 leading-snug" data-testid="try-scanner-valueprop">
        {t.vpPre}<span className="text-[#3f6212]">{t.vpHi}</span>{t.vpPost}
      </p>

      {!result?.success && !capped && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !scanning && inputRef.current?.click()}
          className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#84CC16] bg-[#84CC16]/5' : 'border-gray-300 bg-gray-50'}`}
          data-testid="try-scanner-dropzone"
        >
          <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="try-scanner-input" />
          {scanning ? (
            <div className="flex flex-col items-center gap-3 text-[#064E3B]">
              <Loader2 className="h-8 w-8 animate-spin text-[#84CC16]" />
              <p className="font-semibold">{t.reading}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-2xl bg-[#064E3B]/10 flex items-center justify-center mb-1"><Upload className="h-7 w-7 text-[#064E3B]" /></div>
              <p className="font-bold text-[#064E3B]">{t.dropTitle}</p>
              <p className="text-sm text-gray-500">{t.dropSub}</p>
            </div>
          )}
        </div>
      )}

      {capped && !result?.success && (
        <div className="rounded-2xl bg-[#064E3B] p-8 text-center" data-testid="try-scanner-capped">
          <Sparkles className="h-8 w-8 text-[#84CC16] mx-auto mb-3" />
          <p className="font-bold text-white mb-1">{t.cappedTitle}</p>
          <p className="text-sm text-[#A7F3D0] mb-5">{t.cappedSub}</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7" data-testid="try-scanner-capped-signup">
              {t.signupFree} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      )}

      {error && !capped && <p className="text-sm text-amber-600 mt-4 text-center" data-testid="try-scanner-error">{error}</p>}

      {result?.success && (
        <div data-testid="try-scanner-result">
          <div className="grid sm:grid-cols-[120px_1fr] gap-4 items-center">
            {preview ? (
              <img src={preview} alt="receipt" className="w-full max-w-[120px] rounded-xl border border-gray-200 object-cover max-h-32 mx-auto" />
            ) : (
              <div className="w-[120px] h-28 rounded-xl bg-gray-100 flex items-center justify-center mx-auto"><ScanLine className="h-8 w-8 text-gray-400" /></div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label={t.fMerchant} value={result.merchant || '—'} />
              <Field label={t.fAmount} value={result.amount != null ? `${sym}${result.amount}` : '—'} strong />
              <Field label={t.fDate} value={result.date || '—'} />
              <Field label={t.fCategory} value={result.category || '—'} />
              <Field label={t.fTax} value={result.tax != null ? `${sym}${result.tax}` : '—'} />
              <Field label={t.fCurrency} value={result.currency || '—'} />
            </div>
          </div>
          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{t.downloadAs}</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadCsv} variant="outline" className="border-[#064E3B]/30 text-[#064E3B] font-bold rounded-full" data-testid="try-scanner-download">
                <FileDown className="h-4 w-4 mr-1" /> {t.csvFree}
              </Button>
              <Link to="/auth">
                <Button variant="outline" className="border-gray-300 text-gray-500 font-bold rounded-full" data-testid="try-scanner-download-excel">
                  <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel <Lock className="h-3 w-3 ml-1.5" />
                </Button>
              </Link>
              <Link to="/auth">
                <Button variant="outline" className="border-gray-300 text-gray-500 font-bold rounded-full" data-testid="try-scanner-download-pdf">
                  <FileText className="h-4 w-4 mr-1" /> PDF <Lock className="h-3 w-3 ml-1.5" />
                </Button>
              </Link>
            </div>
            <p className="text-xs text-gray-400 mt-2">{t.exportsFree}</p>
            <Link to="/auth" className="block mt-3">
              <Button className="w-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full" data-testid="try-scanner-signup">
                {t.signupExport} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
          <button onClick={() => { setResult(null); setPreview(null); setError(''); }} className="text-xs text-gray-400 hover:text-[#064E3B] mt-3 underline" data-testid="try-scanner-again">{t.scanAnother}</button>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, strong }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-gray-400">{label}</p>
    <p className={`text-[#064E3B] ${strong ? 'font-bold text-base' : 'font-medium'}`}>{value}</p>
  </div>
);

export default TryScanner;
